import {Component, input, output} from '@angular/core';
import {EDITOR_ACCENT, FloorPlanBackground} from '../shared/models/table-layout.model';
import {MIN_BACKGROUND_WIDTH} from '../shared/models/floor-plan-geometry';

/**
 * The **body** of the floor-plan inspector while the background image is selected (#201) — a literal port of the
 * design handover's `insp.isBg` branch: the opacity slider, the exact width, the placement note and *Bild ersetzen*.
 *
 * The panel around it (icon, kind label, static "Bild" title, close button, Löschen) belongs to the host screen,
 * which also owns the hidden file input the header's *Bild* chip and this component's replace button both open.
 *
 * The **height is deliberately not editable** — it always follows from the width and the decoded image's aspect
 * ratio (the handover's §8, and the backend has no `Height` column at all), so there is no pair of numbers that can
 * drift out of proportion. The image is positioned by dragging its frame on the canvas, which is why there are no
 * x/y fields here either.
 *
 * Fully controlled: it never fetches, uploads or mutates anything itself.
 */
@Component({
  selector: 'app-background-editor',
  standalone: true,
  imports: [],
  styleUrl: './floor-plan-editor.scss',
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .tp-opacity-head {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      margin-bottom: 8px;
    }

    .tp-opacity-head > label {
      font-size: 13.5px;
      font-weight: 600;
    }

    .tp-opacity-value {
      font-size: 13px;
      font-weight: 600;
      color: #c50e6c;
    }

    .tp-replace {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      padding: 10px;
      border-radius: 11px;
      background: #f0efed;
      font-weight: 600;
      font-size: 13.5px;
      color: #3d3d40;
    }

    .tp-replace:disabled {
      opacity: .4;
      cursor: not-allowed;
    }
  `],
  template: `
    @if (background(); as bg) {
      <div>
        <div class="tp-opacity-head">
          <label for="bg-opacity">Deckkraft</label>
          <span class="tp-opacity-value">{{ opacityPercent(bg) }} %</span>
        </div>
        <input id="bg-opacity" class="cq-range" type="range" min="10" max="100" step="1"
               [value]="opacityPercent(bg)" [disabled]="busy()" (input)="changeOpacity($event)"/>
      </div>

      <div>
        <label class="tp-section-title" for="bg-width">
          Breite <span class="tp-section-muted">(Höhe folgt automatisch)</span>
        </label>
        <input id="bg-width" class="cq-in" type="number" [attr.min]="minWidth" step="10"
               [value]="roundedWidth()" [disabled]="busy()" (input)="changeWidth($event)"/>
      </div>

      <p class="tp-note">Liegt unter dem Raster. Rahmen ziehen zum Verschieben, Ecke zum Skalieren.</p>

      <button type="button" class="cq-btn tp-replace" [disabled]="busy()" (click)="replaceRequested.emit()">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 12a8 8 0 0 1 13-6.2L20 8M20 4v4h-4"/>
          <path d="M20 12a8 8 0 0 1-13 6.2L4 16M4 20v-4h4"/>
        </svg>
        Bild ersetzen
      </button>
    }
  `
})
export class BackgroundEditorComponent {
  /** The stored background, or `null` when the shard has none. */
  readonly background = input<FloorPlanBackground | null>(null);
  /** The width the canvas is actually rendering at (resolves the "not placed yet" `width === 0` case). */
  readonly renderedWidth = input(0);
  /** Whether an upload/save is in flight, which disables every control. */
  readonly busy = input(false);

  /** A placement change. `width` is in centimetres; the height is never sent. */
  readonly placementChanged = output<{width?: number; opacity?: number}>();
  /** The admin asked to pick a replacement file; the host owns the file input. */
  readonly replaceRequested = output<void>();

  protected readonly accent = EDITOR_ACCENT;
  protected readonly minWidth = MIN_BACKGROUND_WIDTH;

  protected roundedWidth(): number {
    return Math.round(this.renderedWidth());
  }

  protected opacityPercent(background: FloorPlanBackground): number {
    return Math.round(background.opacity * 100);
  }

  protected changeOpacity(event: Event): void {
    const percent = Number.parseInt((event.target as HTMLInputElement).value, 10);
    if (!Number.isFinite(percent)) return;
    this.placementChanged.emit({opacity: Math.min(1, Math.max(0.1, percent / 100))});
  }

  protected changeWidth(event: Event): void {
    const width = Number.parseInt((event.target as HTMLInputElement).value, 10);
    if (!Number.isFinite(width)) return;
    this.placementChanged.emit({width: Math.max(MIN_BACKGROUND_WIDTH, width)});
  }
}
